/**
 * PatrolsTabV4
 *
 * Hybrid layout for patrols combining list and detail views.
 * Replaces the swipeable card interface with a dispatcher-style dashboard.
 *
 * Visual Design:
 * ┌─────────────────────────────────────────────────┐
 * │  ┌─ Patrol List ───┐ ┌─ Patrol Detail ────────┐ │
 * │  │ ● Downtown      │ │ 📍 Downtown Office     │ │
 * │  │   2:00 PM       │ │ Guard: John Smith      │ │
 * │  │   In Progress   │ │ Progress ████░░ 5/12   │ │
 * │  ├─────────────────┤ ├────────────────────────┤ │
 * │  │ ○ Mall North    │ │ ▼ Exterior Checklist   │ │
 * │  │   4:00 PM       │ │   ✓ Front entrance     │ │
 * │  │   Scheduled     │ │   ⚠ Back door - "ajar" │ │
 * │  ├─────────────────┤ │ ▶ Interior Checklist   │ │
 * │  │ ○ Warehouse     │ ├────────────────────────┤ │
 * │  │   6:00 PM       │ │    [Complete Patrol]   │ │
 * │  └─────────────────┘ └────────────────────────┘ │
 * └─────────────────────────────────────────────────┘
 *
 * Mobile Layout:
 * - List and detail stack vertically
 * - List is collapsible/scrollable
 */

import React, { useMemo, useCallback, useState } from 'react';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import styles from './PatrolsTabV4.module.css';
import PatrolList from '../../patrolList/PatrolList';
import PatrolDetailPanel from '../../patrolDetail/PatrolDetailPanel';
import { useScheduleContext } from '../../../context/ScheduleProvider';
import { useGuardRoleContext } from '../../../context/GuardRoleProvider';
import { useStartButtonState } from '../../../hooks/useStartButtonState';
import { completePatrol as completePatrolApi } from 'api/dispatch';
import { getPatrolData } from 'api/canguard/getPatrolData';
import { useNotificationProvider } from 'hooks';
import InfoComponent from '../../infoComponent/infoComponnet';

// ============================================
// Types
// ============================================

type PatrolStatus = 'pending' | 'in_progress' | 'completed';

interface PatrolsTabV4Props {
  /** Optional: group patrols by customer in the list */
  groupByCustomer?: boolean;
}

// ============================================
// Utility Functions
// ============================================

/**
 * Determine patrol status from ScheduleAssignment
 */
function getPatrolStatus(patrol: ScheduleAssignment): PatrolStatus {
  if (patrol.complete) return 'completed';
  if (patrol.started) return 'in_progress';
  return 'pending';
}

/**
 * Format the patrol start time for display
 */
function getStartedAt(patrol: ScheduleAssignment): string | null {
  if (patrol.patrol_begin_time) {
    return new Date(patrol.patrol_begin_time).toISOString();
  }
  return null;
}

// ============================================
// Loading Skeleton
// ============================================

const LoadingSkeleton: React.FC = () => (
  <Box className={styles.pageContainer}>
    {/* Title Bar Skeleton */}
    <Paper className={styles.titleBar} elevation={0}>
      <Skeleton variant="text" width={150} height={32} />
      <Skeleton variant="text" width={200} height={20} />
    </Paper>

    {/* Main Content Skeleton */}
    <Box className={styles.mainContent}>
      <Paper className={styles.listPanel} elevation={1}>
        <Box sx={{ p: 2 }}>
          <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={80}
              sx={{ mb: 1 }}
            />
          ))}
        </Box>
      </Paper>
      <Paper className={styles.detailPanel} elevation={1}>
        <Skeleton variant="rounded" height={6} />
        <Box sx={{ p: 2 }}>
          <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={200} />
        </Box>
      </Paper>
    </Box>
  </Box>
);

// ============================================
// Main Component
// ============================================

const PatrolsTabV4: React.FC<PatrolsTabV4Props> = ({
  groupByCustomer = false,
}) => {
  const {
    schedule,
    isLoadingSchedule,
    selectedPatrolId,
    setSelectedPatrolId,
    fetchSchedule,
  } = useScheduleContext();

  const { isAdmin } = useGuardRoleContext();

  // Find the currently selected patrol
  const selectedPatrol = useMemo(() => {
    if (!selectedPatrolId || !schedule.length) return null;
    return schedule.find(
      (p) => p.schedule_assignment_id === selectedPatrolId
    ) || null;
  }, [schedule, selectedPatrolId]);

  // Derive props for the detail panel from the selected patrol
  const detailPanelProps = useMemo(() => {
    if (!selectedPatrol) {
      return {
        scheduleAssignmentId: null,
        patrolId: null,
        guardName: '',
        siteName: '',
        address: '',
        patrolStatus: 'pending' as PatrolStatus,
        startedAt: null,
      };
    }

    return {
      scheduleAssignmentId: selectedPatrol.schedule_assignment_id,
      patrolId: selectedPatrol.schedule_assignment_id,
      guardName: selectedPatrol.full_name || '',
      siteName: selectedPatrol.cust_name || '',
      address: selectedPatrol.address || '',
      patrolStatus: getPatrolStatus(selectedPatrol),
      startedAt: getStartedAt(selectedPatrol),
    };
  }, [selectedPatrol]);

  // Handle patrol selection from list
  const handlePatrolSelect = (patrolId: number) => {
    setSelectedPatrolId(patrolId);
  };

  // Determine if editing is allowed:
  // - Admins can always edit
  // - Guards can only edit their own in-progress patrols
  const canEdit = useMemo(() => {
    if (isAdmin) return true;
    if (!selectedPatrol) return false;
    return selectedPatrol.started && !selectedPatrol.complete; // in_progress
  }, [isAdmin, selectedPatrol]);

  // Hook now handles null patrol gracefully
  const startButtonState = useStartButtonState(selectedPatrol, isAdmin);

  // Notification hooks
  const { notifySuccess, notifyError } = useNotificationProvider();

  // Start patrol functionality - uses API directly (no drawer)
  const [isStarting, setIsStarting] = useState(false);

  const handleStartPatrol = useCallback(async () => {
    if (!selectedPatrol) return;

    setIsStarting(true);
    try {
      // Initialize patrol data (creates comment_list record if needed)
      await getPatrolData(selectedPatrol);
      notifySuccess('Patrol started');
    } catch (error) {
      console.error('[PatrolsTabV4/handleStartPatrol] Failed to start patrol:', error);
      notifyError('Failed to start patrol');
    } finally {
      // Always refresh schedule and wait for it to complete
      await fetchSchedule();
      setIsStarting(false);
    }
  }, [selectedPatrol, notifySuccess, notifyError, fetchSchedule]);

  // Complete patrol functionality
  const [isCompleting, setIsCompleting] = useState(false);

  const handleCompletePatrol = useCallback(
    async (_patrolId: number, _note?: string) => {
      if (!selectedPatrol?.comment_list_id) {
        notifyError('Cannot complete patrol: no active patrol record');
        return;
      }
      setIsCompleting(true);
      try {
        const hasStartDate = !!selectedPatrol.patrol_begin_time;
        await completePatrolApi(selectedPatrol.comment_list_id, hasStartDate);
        notifySuccess('Patrol completed successfully');
      } catch (error: any) {
        console.error('[PatrolsTabV4/handleCompletePatrol] Failed to complete patrol:', error);
        notifyError('Failed to complete patrol');
      } finally {
        // Always refresh schedule and wait for it to complete
        await fetchSchedule();
        setIsCompleting(false);
      }
    },
    [selectedPatrol, notifySuccess, notifyError, fetchSchedule]
  );

  // Loading state
  if (isLoadingSchedule) {
    return <LoadingSkeleton />;
  }

  // Empty state - no patrols scheduled
  if (schedule.length === 0) {
    return <InfoComponent />;
  }

  // Calculate stats for the title bar
  const completedCount = schedule.filter((p) => p.complete).length;
  const inProgressCount = schedule.filter((p) => p.started && !p.complete).length;

  return (
    <Box className={styles.pageContainer}>
      {/* Page Title & Stats */}
      <Paper className={styles.titleBar} elevation={0}>
        <Box className={styles.titleSection}>
          <Typography variant="h5" component="h1" className={styles.pageTitle}>
            My Patrols
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {schedule.length} patrol{schedule.length !== 1 ? 's' : ''} scheduled
            {inProgressCount > 0 && ` · ${inProgressCount} in progress`}
            {completedCount > 0 && ` · ${completedCount} completed`}
          </Typography>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box className={styles.mainContent}>
        {/* Patrol List Panel */}
        <Paper className={styles.listPanel} elevation={1}>
          <PatrolList
            patrols={schedule}
            selectedId={selectedPatrolId}
            onSelect={handlePatrolSelect}
            showSearch={schedule.length > 5}
            groupByCustomer={groupByCustomer}
          />
        </Paper>

        {/* Patrol Detail Panel */}
        <Paper className={styles.detailPanel} elevation={1}>
          <PatrolDetailPanel
            scheduleAssignmentId={detailPanelProps.scheduleAssignmentId}
            patrolId={detailPanelProps.patrolId}
            guardName={detailPanelProps.guardName}
            siteName={detailPanelProps.siteName}
            address={detailPanelProps.address}
            patrolStatus={detailPanelProps.patrolStatus}
            startedAt={detailPanelProps.startedAt}
            allowEdit={canEdit}
            onStartPatrol={selectedPatrol && !selectedPatrol.complete ? handleStartPatrol : undefined}
            startButtonDisabled={startButtonState.disabled || isStarting}
            startButtonReason={isStarting ? 'Starting patrol...' : startButtonState.reason}
            onCompletePatrol={handleCompletePatrol}
            isCompleting={isCompleting}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default PatrolsTabV4;
